import React, { useState } from 'react';
import { ArrowUpRight, Phone } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { PHONE, PHONE_HREF, FORM_STEPS, PROPERTY_TYPES, URGENCY_TYPES } from '../mock';

const inputBase =
  'w-full bg-transparent border-b border-[var(--line-strong)] py-3 text-[15px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--accent)] transition-colors';

export default function CalloutForm() {
  const [form, setForm] = useState({
    name: '', phone: '', postcode: '', email: '',
    property: PROPERTY_TYPES[0], urgency: URGENCY_TYPES[0], symptom: '', notes: '',
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.postcode) {
      toast({ title: 'A few details missing', description: 'Name, phone and postcode help us dispatch fast.' });
      return;
    }
    toast({ title: 'Callout logged — we’ll ring back within 5 minutes.', description: `${form.name} · ${form.postcode} · ${form.urgency}` });
    setForm({ name: '', phone: '', postcode: '', email: '', property: PROPERTY_TYPES[0], urgency: URGENCY_TYPES[0], symptom: '', notes: '' });
  };

  return (
    <section id="request" className="relative py-28 md:py-36 bg-[var(--bg-alt)] border-t border-[var(--line)]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-[0.8fr_1.2fr] gap-14">
        {/* Left copy */}
        <div data-reveal>
          <div className="label-mono text-[var(--accent)] mb-6">File a Callout</div>
          <h2 className="font-display text-[clamp(2.4rem,5.5vw,4.4rem)] text-white">
            Tell us where <span className="font-display-italic text-[#c3cbdb]">it’s warm.</span>
          </h2>
          <p className="mt-8 text-[var(--muted)] text-[15px]">
            Prefer the phone?{' '}
            <a href={PHONE_HREF} className="text-white link-sweep">{PHONE}</a>{' '}
            — answered 24 hours a day, every day.
          </p>

          <div className="mt-12">
            <div className="label-mono text-[var(--muted)] mb-6">What happens next</div>
            <ul className="flex flex-col gap-5">
              {FORM_STEPS.map((s) => (
                <li key={s.n} className="flex items-start gap-4">
                  <span className="label-mono text-[var(--accent)] mt-1">{s.n}</span>
                  <span className="text-[15px] text-[#c3cbdb]">{s.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right form */}
        <form onSubmit={submit} className="border border-[var(--line)] bg-[var(--card)] p-7 md:p-10" data-reveal data-delay="90">
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-7">
            <Field label="Name">
              <input className={inputBase} placeholder="Jane Doe" value={form.name} onChange={set('name')} />
            </Field>
            <Field label="Phone">
              <input className={inputBase} placeholder="+44 ..." value={form.phone} onChange={set('phone')} />
            </Field>
            <Field label="Postcode">
              <input className={inputBase} placeholder="EC1V 9BX" value={form.postcode} onChange={set('postcode')} />
            </Field>
            <Field label="Email (optional)">
              <input className={inputBase} placeholder="you@site.com" value={form.email} onChange={set('email')} />
            </Field>
            <Field label="Property">
              <select className={`${inputBase} appearance-none cursor-pointer`} value={form.property} onChange={set('property')}>
                {PROPERTY_TYPES.map((p) => <option key={p} className="bg-[var(--card)]">{p}</option>)}
              </select>
            </Field>
            <Field label="Urgency">
              <select className={`${inputBase} appearance-none cursor-pointer`} value={form.urgency} onChange={set('urgency')}>
                {URGENCY_TYPES.map((u) => <option key={u} className="bg-[var(--card)]">{u}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Symptom (one line)">
                <input className={inputBase} placeholder="No cold air / unit tripping / ice-up..." value={form.symptom} onChange={set('symptom')} />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Anything else?">
                <input className={inputBase} placeholder="Access notes, opening hours, contact on site..." value={form.notes} onChange={set('notes')} />
              </Field>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-6">
            <span className="label-mono text-[var(--muted-2)] normal-case tracking-normal text-[12px]">No spam · No newsletter · Just a ring back.</span>
            <button type="submit" className="btn-accent flex items-center gap-3 rounded-md px-7 py-4 label-mono tracking-[0.14em] text-[0.74rem]">
              <ArrowUpRight size={15} /> Send Callout
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-mono text-[var(--muted)] block mb-1">{label}</span>
      {children}
    </label>
  );
}
